import * as Application from "@-/com.npmjs.expo-application";
import Constants from "@-/com.npmjs.expo-constants";
import * as Device from "@-/com.npmjs.expo-device";
import { getNetworkStateAsync } from "@-/com.npmjs.expo-network";
import * as Updates from "@-/com.npmjs.expo-updates";
import { Dimensions, PixelRatio, Platform } from "@-/com.npmjs.react-native";
import { ENV } from "@-/com.npmjs.varlock/env";
import { BUNDLE_BUILD_TIME, BUNDLE_GIT_COMMIT } from "@-/dev.improve.haniverse.lib.app.build-info";
import {
  deviceSettingsSchemas,
  getDeviceSetting,
  type DeviceSettingKey,
} from "@-/dev.improve.haniverse.lib.device-settings";
import { getSentryLastEventId } from "@-/dev.improve.haniverse.lib.sentry";
import { nativeProcessStats } from "@-/dev.improve.haniverse.modules.process-stats";
import { readPreviousDiagnosticsSession } from "#diagnostics-session-file";
import {
  getDiagnosticsLastProcessSnapshot,
  getDiagnosticsRecordingStartedAt,
  getDiagnosticsRoute,
  getDiagnosticsSessionInfo,
  isDiagnosticsRecording,
  snapshotDiagnostics,
} from "#diagnostics-store";
import type { DiagnosticsReport } from "#diagnostics-types";
import { readHermesRuntimeProperties } from "#js-runtime-stats";

// Settings that are per-actor study state rather than configuration; large and irrelevant to
// a performance investigation.
const EXCLUDED_SETTING_KEYS = new Set<DeviceSettingKey>(["dailyReviewMilestones"]);

const REPORT_LIMITS = { events: 1500, logs: 4000, samples: 2400 } as const;

function optionalIsoDate(value: Date | null | undefined): string | null {
  return value instanceof Date && Number.isFinite(value.getTime()) ? value.toISOString() : null;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function readAppVariant(): string | null {
  const baked = optionalString(Constants.expoConfig?.extra?.appVariant);
  if (baked !== null) {
    return baked;
  }
  try {
    return optionalString(ENV.EXPO_PUBLIC_APP_VARIANT);
  } catch {
    return null;
  }
}

function readReactNativeVersion(): string | null {
  const constants: unknown = Reflect.get(Platform, "constants");
  if (constants === null || typeof constants !== "object") {
    return null;
  }
  const version: unknown = Reflect.get(constants, "reactNativeVersion");
  if (version === null || typeof version !== "object") {
    return null;
  }
  const major = Reflect.get(version, "major");
  const minor = Reflect.get(version, "minor");
  const patch = Reflect.get(version, "patch");
  const prerelease = Reflect.get(version, "prerelease");
  if (typeof major !== "number" || typeof minor !== "number" || typeof patch !== "number") {
    return null;
  }
  return `${major}.${minor}.${patch}${typeof prerelease === "number" || typeof prerelease === "string" ? `-${prerelease}` : ""}`;
}

function readSettings(): Record<string, unknown> {
  const settings: Record<string, unknown> = {};
  for (const key of Object.keys(deviceSettingsSchemas)) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- keys of the schema record
    const settingKey = key as DeviceSettingKey;
    if (EXCLUDED_SETTING_KEYS.has(settingKey)) {
      continue;
    }
    try {
      settings[settingKey] = getDeviceSetting(settingKey);
    } catch {
      settings[settingKey] = "<unreadable>";
    }
  }
  return settings;
}

async function readNetwork(): Promise<DiagnosticsReport["network"]> {
  try {
    const state = await getNetworkStateAsync();
    return {
      isConnected: state.isConnected ?? null,
      isInternetReachable: state.isInternetReachable ?? null,
      type: state.type ?? null,
    };
  } catch {
    return null;
  }
}

function readCurrentProcessSnapshot() {
  const live = (() => {
    if (nativeProcessStats === null) {
      return null;
    }
    try {
      return nativeProcessStats.getSnapshot(12);
    } catch {
      return null;
    }
  })();
  return live ?? getDiagnosticsLastProcessSnapshot();
}

/**
 * Assembles everything the app knows about itself, the device and the current + previous
 * sessions into one JSON-serializable report. Works even when the recorder is off (the session
 * buffers are then empty, but the build / device / settings / live process sections still tell
 * an engineer what they are looking at).
 */
export async function buildDiagnosticsReport(
  options: {
    userNote?: string | null;
  } = {},
): Promise<DiagnosticsReport> {
  const session = getDiagnosticsSessionInfo();
  const buffers = snapshotDiagnostics(REPORT_LIMITS);
  const process = readCurrentProcessSnapshot();
  const window = Dimensions.get("window");
  const recordingStartedAt = getDiagnosticsRecordingStartedAt();
  let previousSession: DiagnosticsReport["previousSession"] = null;
  try {
    previousSession = readPreviousDiagnosticsSession();
  } catch {
    previousSession = null;
  }

  return {
    app: {
      buildNumber: Application.nativeBuildVersion ?? null,
      bundleBuiltAt:
        BUNDLE_BUILD_TIME > 0 ? optionalIsoDate(new Date(BUNDLE_BUILD_TIME * 1000)) : null,
      bundleGitCommit: optionalString(BUNDLE_GIT_COMMIT),
      bundleIdentifier: Application.applicationId ?? null,
      hermes: readHermesRuntimeProperties(),
      isDev: __DEV__,
      reactNativeVersion: readReactNativeVersion(),
      updates: {
        channel: Updates.channel ?? null,
        createdAt: optionalIsoDate(Updates.createdAt),
        isEmbeddedLaunch: Updates.isEmbeddedLaunch,
        isEnabled: Updates.isEnabled,
        runtimeVersion: Updates.runtimeVersion ?? null,
        updateId: Updates.updateId ?? null,
      },
      variant: readAppVariant(),
      version: Application.nativeApplicationVersion ?? null,
    },
    device: {
      brand: Device.brand ?? null,
      deviceType: Device.deviceType === null ? null : String(Device.deviceType),
      deviceYearClass: Device.deviceYearClass ?? null,
      isDevice: Device.isDevice,
      manufacturer: Device.manufacturer ?? null,
      modelId: optionalString(Device.modelId),
      modelName: Device.modelName ?? null,
      osBuildId: Device.osBuildId ?? null,
      osName: Device.osName ?? null,
      osVersion: Device.osVersion ?? null,
      platform: Platform.OS,
      platformApiLevel: Device.platformApiLevel ?? null,
      screen: {
        fontScale: PixelRatio.getFontScale(),
        height: window.height,
        scale: PixelRatio.get(),
        width: window.width,
      },
      supportedCpuArchitectures: Device.supportedCpuArchitectures ?? null,
      totalMemoryBytes: Device.totalMemory ?? null,
    },
    generatedAt: new Date().toISOString(),
    network: await readNetwork(),
    previousSession,
    schemaVersion: 1,
    sentry: { lastEventId: getSentryLastEventId() ?? null },
    session: {
      currentRoute: getDiagnosticsRoute(),
      events: buffers.events,
      isRecording: isDiagnosticsRecording(),
      logs: buffers.logs,
      process,
      recordingStartedAt:
        recordingStartedAt === null ? null : new Date(recordingStartedAt).toISOString(),
      samples: buffers.samples,
      sessionId: session.sessionId,
      uptimeMs: Date.now() - session.startedAt,
    },
    settings: readSettings(),
    storage: {
      diskAvailableBytes: process?.diskAvailableBytes ?? null,
      diskTotalBytes: process?.diskTotalBytes ?? null,
    },
    userNote: options.userNote ?? null,
  };
}
